/**
 * API Client - Frontend
 * Cấu hình Axios cho tất cả các yêu cầu HTTP
 * - Tự động thêm token xác thực vào headers
 * - Theo dõi số lượng yêu cầu đang chờ (dùng để hiển thị loading indicator)
 * - Xử lý lỗi 401 (Unauthorized) - tự động chuyển hướng về login
 */

import axios from 'axios';

// Lấy địa chỉ API base từ biến môi trường hoặc sử dụng mặc định
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// Tạo instance Axios với cấu hình mặc định
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // Hết timeout sau 30 giây
});

// ============ Quản lý trạng thái yêu cầu đang chờ ============
let pendingRequestCount = 0; // Số lượng yêu cầu HTTP đang chờ xử lý
const pendingRequestListeners = new Set<(count: number) => void>(); // Danh sách hàm callback theo dõi

/**
 * Thông báo cho tất cả các listener về số lượng yêu cầu đang chờ
 * (dùng để cập nhật UI loading indicator)
 */
function notifyPendingRequestListeners(): void {
  pendingRequestListeners.forEach((listener) => listener(pendingRequestCount));
}

/**
 * Tăng số lượng yêu cầu đang chờ (gọi khi bắt đầu yêu cầu)
 */
function increasePendingRequestCount(): void {
  pendingRequestCount += 1;
  notifyPendingRequestListeners();
}

/**
 * Giảm số lượng yêu cầu đang chờ (gọi khi yêu cầu hoàn thành)
 */
function decreasePendingRequestCount(): void {
  pendingRequestCount = Math.max(0, pendingRequestCount - 1);
  notifyPendingRequestListeners();
}

/**
 * Lấy số lượng yêu cầu HTTP hiện đang chờ xử lý
 * @returns số lượng yêu cầu đang chờ
 */
export function getPendingRequestCount(): number {
  return pendingRequestCount;
}

/**
 * Đăng ký theo dõi thay đổi số lượng yêu cầu đang chờ
 * @param listener - hàm callback được gọi khi số lượng thay đổi
 * @returns hàm để hủy đăng ký
 */
export function subscribePendingRequests(listener: (count: number) => void): () => void {
  pendingRequestListeners.add(listener);
  listener(pendingRequestCount); // Gọi ngay với giá trị hiện tại
  return () => {
    pendingRequestListeners.delete(listener);
  };
}

// ============ Quản lý xác thực và xử lý lỗi 401 ============
let unauthorizedHandler: (() => void) | null = null; // Hàm callback khi nhận được lỗi 401
let isRedirectingToLogin = false; // Cờ để tránh chuyển hướng nhiều lần

/**
 * Đặt hàm callback xử lý khi nhận được lỗi 401 (Unauthorized)
 * @param handler - hàm callback hoặc null để xóa
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * Đặt token xác thực vào headers của tất cả yêu cầu sau
 * @param token - JWT token hoặc null để xóa
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    // Thêm token vào header Authorization của tất cả yêu cầu
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    // Xóa header Authorization
    delete api.defaults.headers.common.Authorization;
  }
}

// ============ Interceptors - Các middleware xử lý request/response ============

/**
 * Request interceptor - chạy trước khi gửi yêu cầu
 * - Tăng số lượng yêu cầu đang chờ (để hiển thị loading)
 */
api.interceptors.request.use(
  (config) => {
    increasePendingRequestCount();
    return config;
  },
  (error) => {
    decreasePendingRequestCount();
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - chạy khi nhận được phản hồi từ server
 * - Giảm số lượng yêu cầu đang chờ
 * - Xử lý lỗi 401 (token hết hạn/không hợp lệ):
 *   + Gọi unauthorizedHandler nếu được đặt
 *   + Hoặc xóa token và chuyển hướng về login
 */
api.interceptors.response.use(
  (response) => {
    decreasePendingRequestCount();
    return response;
  },
  (error) => {
    decreasePendingRequestCount();

    // Xử lý lỗi 401 (Unauthorized - token hết hạn hoặc không hợp lệ)
    if (error?.response?.status === 401) {
      const requestUrl: string = error?.config?.url || '';
      const isLoginRequest = requestUrl.indexOf('/auth/login') !== -1;

      // Không xử lý lỗi 401 cho chính request login
      if (!isLoginRequest) {
        if (unauthorizedHandler) {
          unauthorizedHandler();
        } else {
          // Xóa token khỏi storage và headers
          setAuthToken(null);
          window.localStorage.removeItem('cms-auth');
        }

        // Chuyển hướng về trang login (chỉ chuyển một lần)
        if (!isRedirectingToLogin && window.location.pathname !== '/login') {
          isRedirectingToLogin = true;
          window.location.replace('/login');
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
